import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API_URL = import.meta.env.VITE_API_URL as string;

interface AccountInfo {
  email:      string;
  plan:       'free' | 'pro';
  hasBilling: boolean;
  createdAt:  number;
}

export default function Account() {
  const { getToken }              = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [account, setAccount]     = useState<AccountInfo | null>(null);
  const [loading, setLoading]     = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError]         = useState('');
  const [banner, setBanner]       = useState('');

  useEffect(() => {
    const checkout = searchParams.get('checkout');
    if (checkout === 'success') {
      setBanner('Payment successful — your account has been upgraded to Pro!');
      setSearchParams({}, { replace: true });
    } else if (checkout === 'cancelled') {
      setBanner('Checkout cancelled — no charge was made.');
      setSearchParams({}, { replace: true });
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const res   = await fetch(`${API_URL}/account`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data  = await res.json() as AccountInfo & { error?: string };
        if (!res.ok) throw new Error(data.error ?? 'Failed to load account');
        setAccount(data);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function handleSubscribe() {
    setActionLoading(true);
    setError('');
    try {
      const token = await getToken();
      const res   = await fetch(`${API_URL}/account/subscribe`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data  = await res.json() as { url?: string; error?: string };
      if (!res.ok) throw new Error(data.error ?? 'Failed to start checkout');
      window.location.href = data.url!;
    } catch (e) {
      setError((e as Error).message);
      setActionLoading(false);
    }
  }

  async function handlePortal() {
    setActionLoading(true);
    setError('');
    try {
      const token = await getToken();
      const res   = await fetch(`${API_URL}/account/portal`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data  = await res.json() as { url?: string; error?: string };
      if (!res.ok) throw new Error(data.error ?? 'Failed to open billing portal');
      window.location.href = data.url!;
    } catch (e) {
      setError((e as Error).message);
      setActionLoading(false);
    }
  }

  return (
    <div style={{ maxWidth: 560, margin: '40px auto', padding: '0 16px' }}>
      <h2 style={{ marginBottom: 24, color: 'var(--brown)' }}>Account</h2>

      {banner && (
        <div style={{
          background: '#d4edda', border: '1px solid #c3e6cb', borderRadius: 8,
          padding: '12px 16px', marginBottom: 20, color: '#155724', fontWeight: 500,
        }}>
          {banner}
        </div>
      )}

      {error && (
        <div style={{
          background: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: 8,
          padding: '12px 16px', marginBottom: 20, color: '#721c24',
        }}>
          {error}
        </div>
      )}

      {loading ? (
        <p style={{ color: 'var(--gray-500)' }}>Loading…</p>
      ) : account ? (
        <>
          {/* Profile card */}
          <div style={{
            background: '#fff', border: '1px solid var(--gray-200)',
            borderRadius: 12, padding: '20px 24px', marginBottom: 20,
          }}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: '.75rem', fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>Email</div>
              <div style={{ fontWeight: 500 }}>{account.email}</div>
            </div>
            <div>
              <div style={{ fontSize: '.75rem', fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>Plan</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{
                  display: 'inline-block',
                  background: account.plan === 'pro' ? 'var(--green)' : 'var(--gray-200)',
                  color:      account.plan === 'pro' ? '#fff' : 'var(--gray-600)',
                  borderRadius: 20, padding: '3px 12px',
                  fontSize: '.8rem', fontWeight: 700, letterSpacing: '.03em',
                }}>
                  {account.plan === 'pro' ? 'Pro' : 'Free'}
                </span>
                {account.plan === 'free' && (
                  <span style={{ fontSize: '.8rem', color: 'var(--gray-500)' }}>
                    1 league · 6 courts · 16 players
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Billing actions */}
          <div style={{
            background: '#fff', border: '1px solid var(--gray-200)',
            borderRadius: 12, padding: '20px 24px',
          }}>
            <h3 style={{ marginTop: 0, marginBottom: 4, fontSize: '1rem', color: 'var(--brown)' }}>Billing</h3>

            {account.plan === 'free' ? (
              <>
                <p style={{ margin: '0 0 16px', fontSize: '.875rem', color: 'var(--gray-600)' }}>
                  Upgrade to <strong>Pro</strong> for unlimited leagues, courts, and players — $12/month.
                </p>
                <button
                  onClick={handleSubscribe}
                  disabled={actionLoading}
                  style={{
                    background: 'var(--green)', color: '#fff', border: 'none',
                    borderRadius: 8, padding: '10px 22px',
                    fontWeight: 700, fontSize: '.9rem', cursor: actionLoading ? 'not-allowed' : 'pointer',
                    opacity: actionLoading ? .6 : 1,
                  }}
                >
                  {actionLoading ? 'Redirecting…' : 'Upgrade to Pro'}
                </button>
              </>
            ) : (
              <>
                <p style={{ margin: '0 0 16px', fontSize: '.875rem', color: 'var(--gray-600)' }}>
                  You're on the <strong>Pro plan</strong>. Manage your subscription, update payment details, or cancel below.
                </p>
                <button
                  onClick={handlePortal}
                  disabled={actionLoading}
                  style={{
                    background: 'var(--brown)', color: '#fff', border: 'none',
                    borderRadius: 8, padding: '10px 22px',
                    fontWeight: 700, fontSize: '.9rem', cursor: actionLoading ? 'not-allowed' : 'pointer',
                    opacity: actionLoading ? .6 : 1,
                  }}
                >
                  {actionLoading ? 'Redirecting…' : 'Manage Billing'}
                </button>
              </>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
